import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { FC, useEffect, useRef } from "react";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  useGetEventCommentsQuery,
  useSubmitEventCommentMutation,
} from "../../../api/network/vmApiService";
import EventCommentMessage from "./eventCommentMessage";
import svgIcons from "../../../assets/svg/svgIcons";
import { toast } from "react-toastify";
import { APP_CONFIG } from "../../../constants/constants";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUser } from "../../../api/types/User";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";

type ModalDeleteConfirmationProps = {
  ticketId: string;
};

const ModalEventComments: FC<ModalDeleteConfirmationProps> = ({ ticketId }) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.history.events.comments",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showEventComments: false }));
  };

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const loggedInUser: TLegacyUser =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);

  const inputRef = React.createRef<HTMLInputElement>();
  const [inputValue, setInputValue] = React.useState("");
  const inputRefreshRef = React.useRef<number>(0);
  const listRef = React.createRef<HTMLDivElement>();
  const listHeight = React.useRef<number>(96 * 4);

  const { data: dataEventComments, isFetching: isFetchingEventComments } =
    useGetEventCommentsQuery(ticketId, {
      skip: !ticketId,
    });
  useEffect(() => {
    setTimeout(() => {
      if (
        !!dataEventComments &&
        !isFetchingEventComments &&
        !!listRef.current
      ) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
        const height = Math.min(
          (window as any).innerHeight - 320,
          listRef.current.scrollHeight
        );
        listHeight.current = height;
      }
    }, 100);
  }, [dataEventComments, isFetchingEventComments]);

  const [
    submitEventCommentAPITrigger,
    { isLoading: isLoadingSubmitEventComment },
  ] = useSubmitEventCommentMutation();

  const submitComment = () => {
    const inputValue = inputRef.current?.value;
    if (!inputValue) {
      toast.error("Please enter a comment and then hit the submit button");
      return;
    }
    const contentWithSenderPrefixed = `[${loggedInUser.userid}||${loggedInUser.displayname || loggedInUser.contactname}]:${inputValue.trim()}`;
    submitEventCommentAPITrigger({
      ticketId,
      content: contentWithSenderPrefixed,
    })
      .unwrap()
      .then((res) => {
        if (res.result) {
          inputRefreshRef.current += 1;
          setInputValue("");
          toast.success("The comment was submitted.");
        } else {
          toast.error("The comment could not be submitted.");
        }
      })
      .catch((e) => {
        if (APP_CONFIG.DEBUG.ALL) console.log("error", e);
        toast.error("The comment could not be submitted.");
      });
  };

  if (modalsState.showEventComments === false) return null;

  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      ></div>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none z-modal">
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[640px] z-modal">
          <form
            className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={hideModal}
          >
            <h2 className="col-span-6 text-3xl font-bold text-heading-black flex">
              <span className="flex-grow">{t("heading")}</span>
              <button
                className="flex items-center justify-center size-10"
                type="button"
                onClick={hideModal}
                disabled={isLoadingSubmitEventComment}
              >
                <img
                  src={svgIcons.CloseIcon}
                  className="size-8 hover:opacity-40"
                  alt="close"
                />
              </button>
            </h2>

            <div
              className={`col-span-6 flex flex-col gap-4 relative Xmax-h-[${listHeight.current}px] Xmax-h-96 overflow-auto scroll-smooth`}
              style={{ height: `${listHeight.current}px` }}
              ref={listRef}
            >
              {isFetchingEventComments ? (
                <img
                  src={svgIcons.LoadingAnimation}
                  className="absolute size-12 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                />
              ) : !!dataEventComments && dataEventComments.data.length > 0 ? (
                <>
                  {(dataEventComments?.data).map((msg, index) => (
                    <EventCommentMessage message={msg} key={index} />
                  ))}
                </>
              ) : (
                <p className="absolute text-heading-gray888/60 text-xl font-semibold top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  {t("no_comments_yet")}
                </p>
              )}
            </div>

            <div className="col-span-6 flex gap-4">
              <AdminFormFieldInput
                ref={inputRef}
                key={`input-field-${inputRefreshRef.current}`} // added ref to make the form refresh and re-render this part
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                label={""}
                type={"text"}
                id={"comment_input"}
                name={"comment_input"}
                placeholder={t("input_placeholder")}
                customWrapperClass="flex-grow"
                innerWrapperClass="mt-0 relative flex w-full"
                disabled={isLoadingSubmitEventComment}
              />
              <AdminFormFieldSubmit
                label={tMain("submit")}
                type="button"
                variant="primary"
                onClick={submitComment}
                buttonDimensionClass="h-11 w-full px-4"
                customWrapperClass="w-24"
                disabled={isLoadingSubmitEventComment}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalEventComments;
